import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';
import { statusLabels, getStatusStyle } from '../config/statusColors';

const AdminProjects = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    project: null,
    forceDelete: false
  });

  const limit = 20;

  // 프로젝트 목록 조회
  const { data, isLoading } = useQuery(
    ['adminProjects', searchTerm, currentPage],
    () => api.get('/projects', { 
      params: { 
        search: searchTerm, 
        page: currentPage,
        limit 
      } 
    }).then(res => res.data)
  );

  // 프로젝트 삭제 mutation
  const deleteMutation = useMutation(
    ({ projectId, forceDelete }) => 
      api.delete(`/delete/project/${projectId}`, { data: { forceDelete } }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('adminProjects');
        toast.success('프로젝트가 삭제되었습니다');
        setDeleteModal({ show: false, project: null, forceDelete: false });
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.error || '삭제 중 오류가 발생했습니다';
        const details = error.response?.data?.details;
        
        if (details && !deleteModal.forceDelete) {
          // 리워드 관련 경고
          toast.error(
            <div>
              <p>{errorMessage}</p>
              <p className="text-sm mt-1">
                획득: {details.earned?.toLocaleString()}원
                {details.used > 0 && `, 사용: ${details.used.toLocaleString()}원`}
                {details.withdrawn > 0 && `, 인출: ${details.withdrawn.toLocaleString()}원`}
              </p>
              <p className="text-xs mt-1">강제 삭제하려면 다시 시도하세요</p>
            </div>,
            { duration: 5000 }
          );
          setDeleteModal(prev => ({ ...prev, forceDelete: true }));
        } else {
          toast.error(errorMessage);
        }
      }
    }
  );

  const handleDelete = () => {
    if (!deleteModal.project) return;
    
    deleteMutation.mutate({
      projectId: deleteModal.project.id,
      forceDelete: deleteModal.forceDelete
    });
  };

  const projects = data?.projects || [];
  const totalPages = data?.pages || 1;

  const getStatusBadge = (status) => {
    const text = statusLabels[status] || '알 수 없음';
    const style = getStatusStyle(status, 'light');
    return { text, style };
  };

  // 검색 시 첫 페이지로 리셋
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">프로젝트 완전삭제</h1>
        {/* 검색 */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="프로젝트 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* 프로젝트 목록 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    프로젝트
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    고객
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    소장
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    계약금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    생성일
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    작업
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {projects.map((project) => {
                  const { text: statusText, style: statusStyle } = getStatusBadge(project.status);
                  return (
                    <tr key={project.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {project.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            #{project.id}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {project.customer_name || '-'}
                        </div>
                        {project.is_vip && (
                          <span className="text-xs text-yellow-600 font-semibold">VIP</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {project.contractor_name || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusStyle}`}>
                          {statusText}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {project.contract_amount ? 
                          `${project.contract_amount.toLocaleString()}원` : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {format(new Date(project.created_at), 'yyyy-MM-dd')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setDeleteModal({ 
                            show: true, 
                            project, 
                            forceDelete: false 
                          })}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* 페이징 */}
        {totalPages > 1 && (
          <div className="mt-4 flex justify-center">
            <nav className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이전
              </button>
              
              {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = idx + 1;
                } else if (currentPage <= 3) {
                  pageNum = idx + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + idx;
                } else {
                  pageNum = currentPage - 2 + idx;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* 삭제 확인 모달 */}
      {deleteModal.show && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                프로젝트 삭제 확인
              </h3>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                다음 프로젝트를 삭제하시겠습니까?
              </p>
              <div className="bg-gray-50 p-3 rounded">
                <p className="font-medium text-gray-900">
                  {deleteModal.project?.title}
                </p>
                <p className="text-sm text-gray-500">
                  ID: #{deleteModal.project?.id}
                </p>
                {deleteModal.project?.customer_name && (
                  <p className="text-sm text-gray-500">
                    고객: {deleteModal.project.customer_name}
                    {deleteModal.project.is_vip && ' (VIP)'}
                  </p>
                )}
              </div>
            </div>

            {deleteModal.forceDelete && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
                <p className="text-sm text-red-800">
                  <strong>경고:</strong> 이 프로젝트에는 리워드 내역이 있습니다. 
                  강제 삭제 시 VIP 고객의 리워드 잔액이 조정됩니다.
                </p>
              </div>
            )}

            <div className="text-sm text-gray-600 mb-4">
              <p className="font-medium mb-1">삭제되는 데이터:</p>
              <ul className="list-disc list-inside text-xs space-y-1">
                <li>모든 메시지 및 파일</li>
                <li>댓글 및 리뷰</li>
                <li>리워드 내역 (VIP 잔액 조정)</li>
                <li>알림 및 활동 로그</li>
              </ul>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setDeleteModal({ show: false, project: null, forceDelete: false })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  deleteModal.forceDelete 
                    ? 'bg-red-700 hover:bg-red-800' 
                    : 'bg-red-600 hover:bg-red-700'
                } disabled:opacity-50`}
              >
                {deleteMutation.isLoading ? '삭제 중...' : 
                  deleteModal.forceDelete ? '강제 삭제' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProjects;